import { Spacing } from '@persona-id/design-tokens';
import IconPlus from '@persona-id/design-tokens/icons/PlusIcon';
import { useFormState } from 'react-hook-form';
import { FormError } from 'shared/components/FormControl';
import styled from 'styled-components';

import IntegrationHeroCard from 'dashboard/components/IntegrationHeroCard';
import ModalWrapper from 'dashboard/components/ModalWrapper';
import OauthPopup from 'dashboard/components/OauthPopup';
import Form from 'dashboard/layouts/FormLayout/Form';
import { Container, Section } from 'dashboard/layouts/IndexViewLayoutV2';
import {
  ModalBody,
  ModalContainer,
  ModalDescription,
  ModalHeader,
} from 'dashboard/layouts/ModalLayout';
import { ExternalIntegration } from 'dashboard/models/external-integration';
import { CreateExternalIntegrationParams } from 'dashboard/redux/thunk-actions/external-integrations';
import ExternalIntegrationHeader from 'dashboard/views/DashboardExternalIntegrationsShowView/ExternalIntegrationHeader';
import { ProductFeatureContactUsBanner } from 'dashboard/views/DashboardExternalIntegrationsShowView/ProductFeatureContactUsBanner';
import ExternalIntegrationOAuthDetailsTable from 'dashboard/views/DashboardExternalIntegrationsShowView/Productivity/ExternalIntegrationOAuthDetailsTable';
import ExternalIntegrationOAuthErrorModal from 'dashboard/views/DashboardExternalIntegrationsShowView/Productivity/ExternalIntegrationOAuthErrorModal';
import Button from 'design-system/components/Button';
import Input from 'design-system/components/Input';
import Spacer from 'design-system/components/Spacer';
import { HStack } from 'design-system/components/Stack';
import { Label } from 'design-system/components/Text';
import ZendeskIcon from 'images/icons/marketplace/ZendeskIcon.svg';
import {
  LEGACY_EXTERNAL_INTEGRATION_OAUTH_REDIRECT_URL,
  LEGACY_EXTERNAL_INTEGRATION_VENDORS,
  LegacyVendorName,
} from 'lib/constants/externalIntegrationVendors';
import useToggle from 'lib/hooks/useToggle';
import { ApiModel } from 'lib/interfaces/api';
import { useFormSecurely } from 'lib/security/reactHookForm';

export interface ZendeskConfigurationProps {
  editable: boolean;
  isLoading: boolean;
  externalIntegrations: ExternalIntegration[];
  createExternalIntegration: (
    externalIntegrationType: ExternalIntegration['type'],
    integrationParams: CreateExternalIntegrationParams,
  ) => void;
  setSelectedIntegration: (integration: ExternalIntegration) => void;
}

const InputContainer = styled('div')`
  display: flex;
  flex-direction: row;
`;

const StyledInput = styled(Input)`
  margin-right: ${Spacing[2]};
`;

const OAUTH_CLIENT_ID = 'zdg-persona_integration_for_zendesk';
const OAUTH_SCOPE = 'read%20write';

const ZendeskConfiguration = ({
  editable,
  isLoading,
  externalIntegrations,
  createExternalIntegration,
  setSelectedIntegration,
}: ZendeskConfigurationProps) => {
  const [showErrorModal, { enable: openErrorModal, disable: closeErrorModal }] = useToggle(false);

  const [addModal, { enable: showAddModal, disable: closeAddModal }] = useToggle(false);

  const onCode = (code: string, params: URLSearchParams) => {
    const state = params.get('state');
    const subdomain = state ? JSON.parse(state)?.subdomain : null;
    createExternalIntegration(ApiModel.ExternalIntegrationProductivityZendeskOAuth, {
      code: code,
      'redirect-url': LEGACY_EXTERNAL_INTEGRATION_OAUTH_REDIRECT_URL,
      subdomain: subdomain,
    });
  };

  const { register, control, reset, watch } = useFormSecurely<{ subdomain: string }>({
    mode: 'onBlur',
  });
  const watchSubdomain = watch('subdomain');

  const { isSubmitting, isDirty, errors } = useFormState({
    control,
  });

  const submitDisabled = !isDirty || isSubmitting || !!Object.keys(errors).length;

  const generateAuthorizationUrl = (subdomain: string, state: string) => {
    return `https://${subdomain}.zendesk.com/oauth/authorizations/new?redirect_uri=${LEGACY_EXTERNAL_INTEGRATION_OAUTH_REDIRECT_URL}&client_id=${OAUTH_CLIENT_ID}&scope=${OAUTH_SCOPE}&state=${state}&response_type=code`;
  };

  return (
    <Container>
      <ExternalIntegrationOAuthErrorModal isOpen={showErrorModal} handleClose={closeErrorModal} />
      <ModalWrapper isOpen={addModal} handleCloseModal={closeAddModal}>
        <Form>
          <ModalContainer>
            <ModalHeader onClose={closeAddModal}>Connect to Zendesk</ModalHeader>
            <ModalDescription>
              Enter the subdomain of the Zendesk instance to connect to
            </ModalDescription>
            <ModalBody>
              <Label size='lg' weight='bold'>
                Subdomain(required)
              </Label>
              <Spacer height={1} />
              <InputContainer>
                <StyledInput
                  {...register('subdomain', {
                    required: 'Enter the subdomain of the Zendesk instance to connect to',
                  })}
                  type='text'
                  placeholder='subdomain'
                  hasError={!!errors.subdomain}
                  disabled={isSubmitting}
                />

                <Input placeholder='.zendesk.com' disabled={true} />
              </InputContainer>
              {errors.subdomain && <FormError>{errors.subdomain.message}</FormError>}
              <Spacer height={4} />
              <HStack equal={true}>
                <Button
                  id='zendesk-add__cancel-button'
                  type='button'
                  variant='basic'
                  onClick={closeAddModal}
                  aria-label='cancel button'
                >
                  Cancel
                </Button>
                <OauthPopup
                  url={generateAuthorizationUrl(
                    watchSubdomain,
                    JSON.stringify({ url: location.pathname, subdomain: watchSubdomain }),
                  )}
                  onCode={onCode}
                  onError={openErrorModal}
                  onClose={() => {
                    closeAddModal();
                    reset();
                  }}
                  disabled={submitDisabled}
                >
                  <Button aria-label='create button' disabled={submitDisabled}>
                    Create
                  </Button>
                </OauthPopup>
              </HStack>
            </ModalBody>
          </ModalContainer>
        </Form>
      </ModalWrapper>
      <ExternalIntegrationHeader editable={editable}>
        <Button
          id='zendesk-configuration__add-modal-button'
          width='auto'
          prefixIcon={<IconPlus />}
          disabled={!editable}
          aria-label='add button'
          onClick={showAddModal}
        >
          Add Connection
        </Button>
      </ExternalIntegrationHeader>
      <Section>
        <ProductFeatureContactUsBanner />
        <IntegrationHeroCard title='Zendesk' image={ZendeskIcon}>
          {
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            LEGACY_EXTERNAL_INTEGRATION_VENDORS.find(
              (vendor) => vendor.name === LegacyVendorName.Zendesk,
            ).description
          }
        </IntegrationHeroCard>
        <ExternalIntegrationOAuthDetailsTable
          isLoading={isLoading}
          editable={editable}
          displayName='Zendesk'
          connectionName='Instance'
          externalIntegrations={externalIntegrations}
          setSelectedIntegration={setSelectedIntegration}
        />
      </Section>
    </Container>
  );
};

export default ZendeskConfiguration;
